import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { FaInfoCircle, FaSearch, FaTimes, FaArrowUp, FaArrowDown, FaChartLine, FaFilter } from 'react-icons/fa';

const CostVariationReport = () => {
    const [movimentacoes, setMovimentacoes] = useState([]);
    const [dadosGrafico, setDadosGrafico] = useState({ labels: [], datasets: [] });
    const [produtos, setProdutos] = useState([]);
    const [produtosFiltrados, setProdutosFiltrados] = useState([]);
    const [produtoSelecionado, setProdutoSelecionado] = useState(null);
    const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
    const [buscaProduto, setBuscaProduto] = useState("");
    const [dropdownAberto, setDropdownAberto] = useState(false);
    const [variacaoMedia, setVariacaoMedia] = useState(null);
    const [oscilacaoPreco, setOscilacaoPreco] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingProdutos, setLoadingProdutos] = useState(true);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [estatisticas, setEstatisticas] = useState({
        totalEntradas: 0,
        precoMinimo: 0,
        precoMaximo: 0,
        ultimoPreco: 0
    });

    // Detectar se é mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Carregar produtos
    useEffect(() => {
        const carregarProdutos = async () => {
            setLoadingProdutos(true);
            try {
                const response = await api.get('/produtos');
                if (response.data && response.data.length > 0) {
                    const produtosOrdenados = [...response.data].sort((a, b) => 
                        a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'accent' })
                    );
                    setProdutos(produtosOrdenados);
                    setProdutosFiltrados(produtosOrdenados);
                    
                    // Define o primeiro produto como selecionado
                    if (produtosOrdenados.length > 0) {
                        setProdutoSelecionado(produtosOrdenados[0]);
                    }
                } else {
                    setError("Nenhum produto encontrado");
                }
            } catch (error) {
                console.error("Erro ao buscar produtos:", error);
                setError("Erro ao carregar produtos");
            } finally {
                setLoadingProdutos(false);
            }
        };
        carregarProdutos();
    }, []);

    // Filtrar produtos conforme busca
    useEffect(() => {
        if (buscaProduto === "") {
            setProdutosFiltrados(produtos);
        } else {
            const filtrados = produtos.filter(produto => 
                produto.nome.toLowerCase().includes(buscaProduto.toLowerCase()) ||
                produto.codigo_barras?.toLowerCase().includes(buscaProduto.toLowerCase())
            );
            setProdutosFiltrados(filtrados);
        }
    }, [buscaProduto, produtos]);

    // Calcular estatísticas
    const calcularEstatisticas = useCallback((dados) => {
        if (!dados || dados.length === 0) {
            setEstatisticas({
                totalEntradas: 0,
                precoMinimo: 0,
                precoMaximo: 0,
                ultimoPreco: 0
            });
            return;
        }

        const precos = dados.map(mov => mov.preco_custo).filter(p => p > 0);
        
        setEstatisticas({
            totalEntradas: dados.length,
            precoMinimo: precos.length > 0 ? Math.min(...precos) : 0,
            precoMaximo: precos.length > 0 ? Math.max(...precos) : 0,
            ultimoPreco: dados[dados.length - 1]?.preco_custo || 0
        });
    }, []);

    // Processar dados para gráfico e cálculos
    const processarDados = useCallback((dados) => {
        if (!dados || dados.length < 2) {
            setVariacaoMedia(null);
            setOscilacaoPreco(null);
            setDadosGrafico({ labels: [], datasets: [] });
            calcularEstatisticas(dados);
            return;
        }

        const precos = dados.map(mov => mov.preco_custo).filter(p => p > 0);

        if (precos.length < 2) {
            setVariacaoMedia(null);
            setOscilacaoPreco(null);
            setDadosGrafico({ labels: [], datasets: [] });
            calcularEstatisticas(dados);
            return;
        }

        // Cálculo da variação percentual média
        let variacoes = [];
        for (let i = 1; i < precos.length; i++) {
            if (precos[i - 1] > 0) {
                let variacao = ((precos[i] - precos[i - 1]) / precos[i - 1]) * 100;
                variacoes.push(variacao);
            }
        }
        const variacaoMediaCalc = variacoes.length > 0 ? 
            variacoes.reduce((acc, v) => acc + v, 0) / variacoes.length : 0;

        // Cálculo da oscilação no preço
        const media = precos.reduce((acc, p) => acc + p, 0) / precos.length;
        const desvioPadrao = Math.sqrt(
            precos.reduce((acc, p) => acc + Math.pow(p - media, 2), 0) / precos.length
        );
        const oscilacaoPrecoCalc = media > 0 ? (desvioPadrao / media) * 100 : 0;

        setVariacaoMedia(variacaoMediaCalc);
        setOscilacaoPreco(oscilacaoPrecoCalc);
        calcularEstatisticas(dados);

        // Preparar dados do gráfico
        const dadosValidos = dados.filter(mov => mov.preco_custo > 0);
        
        setDadosGrafico({
            labels: dadosValidos.map(mov => {
                const data = new Date(mov.data);
                return data.toLocaleDateString('pt-BR', { 
                    day: '2-digit', 
                    month: '2-digit',
                    year: isMobile ? '2-digit' : 'numeric'
                });
            }),
            datasets: [
                {
                    label: `${produtoSelecionado?.nome || 'Produto'} - Preço de Custo`,
                    data: dadosValidos.map(mov => mov.preco_custo),
                    borderColor: "#5B4740",
                    backgroundColor: "rgba(91, 71, 64, 0.1)",
                    fill: true,
                    tension: 0.3,
                    pointRadius: isMobile ? 4 : 3,
                    pointHoverRadius: isMobile ? 6 : 5,
                    pointBackgroundColor: "#5B4740",
                    pointBorderColor: "#fff",
                    pointBorderWidth: 2,
                },
            ],
        });
    }, [produtoSelecionado, isMobile, calcularEstatisticas]);

    // Carregar movimentações
    const carregarMovimentacoes = useCallback(async () => {
        if (!produtoSelecionado) return;

        setLoading(true);
        setError(null);
        
        try {
            const response = await api.get('/movimentacoes?format=legacy');
            
            if (!response.data || !Array.isArray(response.data)) {
                throw new Error("Dados de movimentações inválidos");
            }

            const movimentacoesFiltradas = response.data
                .filter(mov => {
                    const dataMovimentacao = new Date(mov.data_hora);
                    const anoMovimentacao = dataMovimentacao.getFullYear();
                    
                    return (
                        mov.tipo === 'entrada' &&
                        anoMovimentacao === parseInt(filtroAno) &&
                        mov.produto_id === produtoSelecionado.id &&
                        mov.preco_custo != null &&
                        !mov.revertida
                    );
                })
                .map(mov => ({
                    ...mov,
                    preco_custo: parseFloat(mov.preco_custo) || 0,
                    data: mov.data_hora?.split(' ')[0] || "",
                }))
                .sort((a, b) => new Date(a.data) - new Date(b.data));

            setMovimentacoes(movimentacoesFiltradas);
            processarDados(movimentacoesFiltradas);
            
        } catch (error) {
            console.error("Erro ao buscar movimentações:", error);
            setError("Erro ao carregar movimentações. Tente novamente.");
            setMovimentacoes([]);
            processarDados([]);
        } finally {
            setLoading(false);
        }
    }, [filtroAno, produtoSelecionado, processarDados]);

    // Recarregar dados quando filtros mudarem
    useEffect(() => {
        if (produtoSelecionado) {
            carregarMovimentacoes();
        }
    }, [carregarMovimentacoes]);

    // Função para selecionar produto
    const selecionarProduto = (produto) => {
        setProdutoSelecionado(produto);
        setDropdownAberto(false);
        setBuscaProduto("");
    };

    // Limpar busca
    const limparBusca = () => {
        setBuscaProduto("");
        setDropdownAberto(false);
    };

    // Opções do gráfico responsivo
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index'
        },
        plugins: {
            legend: {
                display: !isMobile,
                position: 'top',
                labels: {
                    font: {
                        size: isMobile ? 10 : 12
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `R$ ${context.parsed.y.toFixed(2)}`;
                    }
                }
            }
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: false
                },
                ticks: {
                    maxRotation: isMobile ? 45 : 0,
                    minRotation: isMobile ? 45 : 0,
                    font: {
                        size: isMobile ? 10 : 11
                    }
                }
            },
            y: {
                grid: {
                    color: 'rgba(0,0,0,0.1)'
                },
                ticks: {
                    callback: function(value) {
                        return `R$ ${value.toFixed(2)}`;
                    },
                    font: {
                        size: isMobile ? 10 : 11
                    }
                }
            }
        }
    };

    if (loadingProdutos) {
        return (
            <div className="max-w-full md:max-w-6xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                    <p className="mt-2 text-gray-600">Carregando produtos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-full md:max-w-6xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <div className="flex items-center gap-3 mb-4 md:mb-0">
                    <FaChartLine className="text-red-500 text-2xl" />
                    <h2 className="text-xl md:text-2xl font-semibold text-gray-700">
                        Variação do Preço de Custo
                    </h2>
                </div>
                
                {produtoSelecionado && (
                    <div className="text-sm text-gray-500">
                        Produto: <span className="font-medium">{produtoSelecionado.nome}</span>
                    </div>
                )}
            </div>

            {/* Filtros Aprimorados */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-4">
                    <FaFilter className="text-gray-500" />
                    <h3 className="font-medium text-gray-700">Filtros</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Seletor de Produto Aprimorado */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Produto:</label>
                        <div className="relative">
                            <div
                                className="border px-4 py-2 rounded-lg w-full cursor-pointer bg-white flex items-center justify-between"
                                onClick={() => setDropdownAberto(!dropdownAberto)}
                            >
                                <span className={produtoSelecionado ? "text-gray-800" : "text-gray-400"}>
                                    {produtoSelecionado ? produtoSelecionado.nome : "Selecione um produto"}
                                </span>
                                <FaSearch className="text-gray-400" />
                            </div>
                            
                            {dropdownAberto && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
                                    <div className="p-3 border-b">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Buscar por nome ou código..."
                                                value={buscaProduto}
                                                onChange={(e) => setBuscaProduto(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md text-sm pr-8"
                                                autoFocus
                                            />
                                            {buscaProduto && (
                                                <button
                                                    onClick={limparBusca}
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                >
                                                    <FaTimes size={12} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="max-h-48 overflow-y-auto">
                                        {produtosFiltrados.length > 0 ? (
                                            produtosFiltrados.map(produto => (
                                                <div
                                                    key={produto.id}
                                                    className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 ${
                                                        produtoSelecionado?.id === produto.id ? 'bg-blue-50 text-blue-700' : ''
                                                    }`}
                                                    onClick={() => selecionarProduto(produto)}
                                                >
                                                    <div className="font-medium">{produto.nome}</div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Código: {produto.codigo_barras}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-4 py-3 text-gray-500 text-center">
                                                Nenhum produto encontrado
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Seletor de Ano */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Ano:</label>
                        <select
                            value={filtroAno}
                            onChange={(e) => setFiltroAno(Number(e.target.value))}
                            className="border px-4 py-2 rounded-lg w-full"
                            disabled={loading}
                        >
                            {Array.from({ length: 5 }, (_, i) => {
                                const ano = new Date().getFullYear() - i;
                                return (
                                    <option key={ano} value={ano}>
                                        {ano}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </div>
            </div>

            {/* Cards de Estatísticas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-blue-600 text-sm font-medium">Total de Entradas</div>
                    <div className="text-xl font-bold text-blue-800">{estatisticas.totalEntradas}</div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-green-600 text-sm font-medium">Menor Preço</div>
                    <div className="text-lg font-bold text-green-800">
                        R$ {estatisticas.precoMinimo.toFixed(2)}
                    </div>
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg">
                    <div className="text-red-600 text-sm font-medium">Maior Preço</div>
                    <div className="text-lg font-bold text-red-800">
                        R$ {estatisticas.precoMaximo.toFixed(2)}
                    </div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-purple-600 text-sm font-medium">Último Preço</div>
                    <div className="text-lg font-bold text-purple-800">
                        R$ {estatisticas.ultimoPreco.toFixed(2)}
                    </div>
                </div>
            </div>

            {/* Métricas de Variação */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-gray-600 font-medium">Variação Média:</span>
                        <div className="relative group">
                            <FaInfoCircle className="text-gray-400 cursor-pointer" />
                            <div className="absolute left-0 md:left-auto md:right-0 top-6 w-64 bg-gray-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                Percentual médio de variação entre compras consecutivas.
                            </div>
                        </div>
                    </div>
                    {variacaoMedia !== null ? (
                        <div className={`flex items-center gap-2 ${variacaoMedia >= 0 ? "text-green-600" : "text-red-600"}`}>
                            {variacaoMedia >= 0 ? <FaArrowUp size={14} /> : <FaArrowDown size={14} />}
                            <span className="text-xl font-bold">{Math.abs(variacaoMedia).toFixed(2)}%</span>
                        </div>
                    ) : (
                        <span className="text-gray-400 text-lg">Dados insuficientes</span>
                    )}
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-gray-600 font-medium">Oscilação no Preço:</span>
                        <div className="relative group">
                            <FaInfoCircle className="text-gray-400 cursor-pointer" />
                            <div className="absolute left-0 md:left-auto md:right-0 top-6 w-64 bg-gray-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                Mede a instabilidade dos preços. Valores acima de 10% indicam alta volatilidade.
                            </div>
                        </div>
                    </div>
                    {oscilacaoPreco !== null ? (
                        <div className={`text-xl font-bold ${oscilacaoPreco >= 10 ? "text-red-600" : "text-green-600"}`}>
                            {oscilacaoPreco.toFixed(2)}%
                        </div>
                    ) : (
                        <span className="text-gray-400 text-lg">Dados insuficientes</span>
                    )}
                </div>
            </div>

            {/* Loading state */}
            {loading && (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <p className="mt-2 text-gray-600 text-sm">Carregando dados...</p>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    <p>{error}</p>
                </div>
            )}

            {/* Gráfico */}
            {!loading && !error && movimentacoes.length > 0 ? (
                <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-700 mb-4">Evolução do Preço de Custo</h3>
                    <div className="h-64 md:h-96 bg-white border rounded-lg p-4">
                        <Line data={dadosGrafico} options={chartOptions} />
                    </div>
                </div>
            ) : !loading && !error && produtoSelecionado && (
                <div className="text-center py-8 text-gray-500 mb-6 bg-gray-50 rounded-lg">
                    <FaChartLine className="mx-auto text-4xl text-gray-400 mb-3" />
                    <p className="text-lg">Nenhum dado disponível</p>
                    <p className="text-sm mt-1">
                        Não há entradas de estoque para <strong>{produtoSelecionado.nome}</strong> em {filtroAno}.
                    </p>
                </div>
            )}

            {/* Tabela de Histórico */}
            {!loading && movimentacoes.length > 0 && (
                <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-4">
                        Histórico de Entradas ({movimentacoes.length} registros)
                    </h3>
                    
                    {/* Desktop: Tabela */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full border-collapse border border-gray-300">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="p-3 text-left border border-gray-300">Data</th>
                                    <th className="p-3 text-right border border-gray-300">Preço de Custo</th>
                                    <th className="p-3 text-center border border-gray-300">Quantidade</th>
                                    <th className="p-3 text-center border border-gray-300">Lote</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movimentacoes.map((mov, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="p-3 border border-gray-300 whitespace-nowrap">
                                            {new Date(mov.data).toLocaleDateString('pt-BR')}
                                        </td>
                                        <td className="p-3 text-right border border-gray-300 text-green-600 font-medium">
                                            R$ {mov.preco_custo.toFixed(2)}
                                        </td>
                                        <td className="p-3 text-center border border-gray-300">
                                            {mov.quantidade}
                                        </td>
                                        <td className="p-3 text-center border border-gray-300 text-sm text-gray-600">
                                            {mov.lote?.numero_lote || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile: Cards */}
                    <div className="md:hidden space-y-3">
                        {movimentacoes.map((mov, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1">
                                        <div className="font-medium text-gray-800">
                                            {new Date(mov.data).toLocaleDateString('pt-BR')}
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            Qtd: {mov.quantidade} | Lote: {mov.lote?.numero_lote || '-'}
                                        </div>
                                    </div>
                                    <div className="text-green-600 font-bold text-lg">
                                        R$ {mov.preco_custo.toFixed(2)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Fechar dropdown ao clicar fora */}
            {dropdownAberto && (
                <div 
                    className="fixed inset-0 z-5" 
                    onClick={() => setDropdownAberto(false)}
                />
            )}
        </div>
    );
};

export default CostVariationReport;